import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom'
import { puedeVerRuta, rutaDeInicio, tieneRecorte } from '@/lib/modulos'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import Sidebar from '@/components/layout/Sidebar'
import ToastContainer from '@/components/layout/Toast'
import AsistenteBubble from '@/components/asistente/AsistenteBubble'
import AuthGuard from '@/components/auth/AuthGuard'
import ChatNotificationProvider from '@/components/layout/ChatNotificationProvider'
import PushSubscriptionManager from '@/components/layout/PushSubscriptionManager'
import ClienteGuard from '@/components/auth/ClienteGuard'
import PWAInstallGate from '@/components/pwa/PWAInstallGate'
import PWALayout from '@/components/pwa/PWALayout'
import { usePWAInstall } from '@/hooks/usePWAInstall'
import Login from '@/pages/Login'
import CambiarPassword from '@/pages/CambiarPassword'
import Restablecer from '@/pages/Restablecer'
import SeleccionarCuenta from '@/pages/SeleccionarCuenta'
import Dashboard from '@/pages/Dashboard'
import Clientes from '@/pages/Clientes'
import VerEmpresa from '@/pages/VerEmpresa'
import Contactos from '@/pages/Contactos'
import NuevoContacto from '@/pages/NuevoContacto'
import NuevaEmpresa from '@/pages/NuevaEmpresa'
import VerContacto from '@/pages/VerContacto'
import Servicios from '@/pages/Servicios'
import Perfiles from '@/pages/Perfiles'
import Configuracion from '@/pages/Configuracion'
import Marcas from '@/pages/Marcas'
import Buzon from '@/pages/Buzon'
import Cotizaciones from '@/pages/Cotizaciones'
import NuevaCotizacion from '@/pages/NuevaCotizacion'
import VerCotizacion from '@/pages/VerCotizacion'
import NuevaCotEquipo from '@/pages/NuevaCotEquipo'
import NuevaCotTareas from '@/pages/NuevaCotTareas'
import DetalleCotTareas from '@/pages/DetalleCotTareas'
import PortalCliente from '@/pages/PortalCliente'
import Facturas from '@/pages/Facturas'
import NuevaFactura from '@/pages/NuevaFactura'
import VerFactura from '@/pages/VerFactura'
import Proyectos from '@/pages/Proyectos'
import VerProyecto from '@/pages/VerProyecto'
import VerTarea from '@/pages/VerTarea'
import Backlog from '@/pages/Backlog'
import DashboardOperaciones from '@/pages/DashboardOperaciones'
import DashboardContabilidad from '@/pages/DashboardContabilidad'
import DashboardCRM from '@/pages/DashboardCRM'
import Pipeline from '@/pages/Pipeline'
import Prospeccion from '@/pages/Prospeccion'
import FlujoCaja from '@/pages/FlujoCaja'
import PagosProgramados from '@/pages/PagosProgramados'
import ExpedientesSunat from '@/pages/ExpedientesSunat'
import ComprobantesAgenda from '@/pages/ComprobantesAgenda'
import PagosRecurrentes from '@/pages/PagosRecurrentes'
import CentralesCosto from '@/pages/CentralesCosto'
import Notificaciones from '@/pages/Notificaciones'
import Conversaciones from '@/pages/Conversaciones'
import ActivityLog from '@/pages/ActivityLog'
import Agentes from '@/pages/Agentes'
import SocialMedia from '@/pages/SocialMedia'
import Wiki from '@/pages/Wiki'
import MarketingDashboard from '@/pages/MarketingDashboard'
import CampanasEmail from '@/pages/CampanasEmail'
import Agendamiento from '@/pages/Agendamiento'
import AgendarPublico from '@/pages/AgendarPublico'
import FlujosMarketing from '@/pages/FlujosMarketing'
import NewsletterSuscriptores from '@/pages/NewsletterSuscriptores'
import Blog from '@/pages/Blog'
import UiKit from '@/pages/UiKit'
import HostingPedidos from '@/pages/HostingPedidos'
import Redivia from '@/pages/Redivia'
import HostingNuevoServicio from '@/pages/HostingNuevoServicio'
import DescargarBackup from '@/pages/DescargarBackup'
import Partners from '@/pages/Partners'
import Accesos from '@/pages/Accesos'
import NuevoPartner from '@/pages/NuevoPartner'
import QRLoginApprove from '@/pages/QRLoginApprove'
import Apps from '@/pages/Apps'

// Portal pages
import PortalLayout from '@/components/portal/PortalLayout'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { puedeVerFacturacion } from '@/lib/portal-permisos'
import PortalDashboard from '@/pages/portal/PortalDashboard'
import ProyectosIndex from '@/pages/portal/ProyectosIndex'
import ProyectoDetalle from '@/pages/portal/ProyectoDetalle'
import PortalHorasPlan from '@/pages/portal/PortalHorasPlan'
import PortalFacturas from '@/pages/portal/PortalFacturas'
import PortalCotizaciones from '@/pages/portal/PortalCotizaciones'
import PortalHosting from '@/pages/portal/PortalHosting'
import PortalApps from '@/pages/portal/PortalApps'
import PortalSuscripcion from '@/pages/portal/PortalSuscripcion'
import PortalAppDetalle from '@/pages/portal/PortalAppDetalle'
import PortalNuevoPedido from '@/pages/portal/PortalNuevoPedido'
import PortalCheckoutHosting from '@/pages/portal/PortalCheckoutHosting'
import PortalCheckoutGracias from '@/pages/portal/PortalCheckoutGracias'
import PortalPerfilLayout from '@/pages/portal/perfil/PortalPerfilLayout'
import PerfilPersonal from '@/pages/portal/perfil/PerfilPersonal'
import PerfilEmpresa from '@/pages/portal/perfil/PerfilEmpresa'
import PerfilEquipo from '@/pages/portal/perfil/PerfilEquipo'
import PerfilSeguridad from '@/pages/portal/perfil/PerfilSeguridad'

// Partner
import PartnerGuard from '@/components/auth/PartnerGuard'
import PartnerLayout from '@/components/partner/PartnerLayout'
import PartnerConfiguracion from '@/pages/partner/PartnerConfiguracion'
import PartnerConfigGeneral from '@/pages/partner/PartnerConfigGeneral'
import PartnerConfigPipeline from '@/pages/partner/PartnerConfigPipeline'
import PartnerPerfil from '@/pages/partner/PartnerPerfil'
import PartnerServicios from '@/pages/partner/PartnerServicios'
import PartnerTareasCrm from '@/pages/partner/PartnerTareasCrm'
import PartnerConversaciones from '@/pages/partner/PartnerConversaciones'
import PartnerCotizaciones from '@/pages/partner/PartnerCotizaciones'
import PartnerHostingCartera from '@/pages/partner/PartnerHostingCartera'

function AppLayout() {
  return (
    <ChatNotificationProvider>
      <PushSubscriptionManager />
      <div className="app" id="app">
        <Sidebar />
        <main className="main-content" id="main-content">
          <Outlet />
        </main>
        <ToastContainer />
        <AsistenteBubble />
      </div>
    </ChatNotificationProvider>
  )
}

/**
 * Guard de módulos: un rol recortado (hoy `ventas`) sólo entra a las pantallas
 * de sus módulos. Cualquier otra ruta lo devuelve a su pantalla de inicio.
 *
 * Es lo que cubre la barra de direcciones: el menú ya no ofrece lo que no es
 * suyo, pero escribir `/contabilidad/caja` a mano seguía montando la página.
 * No reemplaza al portero del backend —ese es el que de verdad niega los
 * datos—; esto evita que alguien se quede mirando una pantalla rota llena de
 * errores 403.
 *
 * Un admin (`modulos` nulo) no pasa por ninguna comprobación.
 */
function ModuloGuard() {
  const { modulos } = useAuth()
  const location = useLocation()
  if (!tieneRecorte(modulos)) return <Outlet />
  if (puedeVerRuta(modulos, location.pathname)) return <Outlet />
  return <Navigate to={rutaDeInicio(modulos)} replace />
}

/**
 * Guard de facturación: Facturas / Cotizaciones / Suscripción son de los
 * contactos PRINCIPAL y FACTURACIÓN. Un TÉCNICO (p. ej. el desarrollador del
 * cliente) que llegue por URL vuelve al resumen. El backend niega igual.
 */
function FacturacionGuard() {
  const { user, contactoId } = useAuth()
  const { afiliaciones, activeClienteId } = usePortalCliente()
  const activeAfil = afiliaciones.find(a => a.cliente_id === activeClienteId)
  const roles: string[] = (activeAfil?.roles as string[] | undefined) ?? user?.roles ?? []
  if (!puedeVerFacturacion(roles, contactoId)) return <Navigate to="/portal" replace />
  return <Outlet />
}

/** Portal index: redirects based on contact role */
function PortalIndex() {
  const { user } = useAuth()
  const roles: string[] = user?.roles ?? []
  const isPrincipal = roles.includes('PRINCIPAL')
  const isFacturacion = roles.includes('FACTURACIÓN') && !isPrincipal
  // FACTURACIÓN-only users go straight to invoices
  if (isFacturacion) return <Navigate to="/portal/facturas" replace />
  return <PortalDashboard />
}

/**
 * El gate de instalacion es para la aplicacion, no para los enlaces publicos.
 * Envolviendo el <Routes> entero tapaba tambien /ver/:token — el link de la
 * cotizacion, que se manda por WhatsApp y se abre casi siempre en celular —
 * con una pantalla de 1Conecta pidiendo instalar el ERP: el cliente no veia la
 * propuesta y, en las cotizaciones de marca blanca, veia la marca del
 * proveedor que el documento oculta a proposito.
 */
function PWAGateLayout() {
  return (
    <PWAInstallGate>
      <Outlet />
    </PWAInstallGate>
  )
}

function AppRoutes() {
  const { isMobile, isStandalone } = usePWAInstall()
  const isPWAMobile = isMobile && isStandalone

  return (
    <Routes>
      {/* Public — no auth required */}
      <Route path="/login" element={<Login />} />
      <Route path="/cambiar-password" element={<CambiarPassword />} />
      <Route path="/restablecer" element={<Restablecer />} />
      <Route path="/seleccionar-cuenta" element={<SeleccionarCuenta />} />
      <Route path="/ver/:token" element={<PortalCliente />} />
      <Route path="/backup/:token" element={<DescargarBackup />} />
      <Route path="/agendar" element={<AgendarPublico />} />
      <Route path="/qr-login/approve/:sessionId" element={<QRLoginApprove />} />

      {/* Todo lo que sigue es la aplicacion: va detras del gate de
          instalacion. Las rutas publicas de arriba quedan fuera. */}
      <Route element={<PWAGateLayout />}>
        {/* Portal — requires authenticated client session */}
        <Route element={<ClienteGuard />}>
          <Route element={<PortalLayout />}>
            <Route path="/portal" element={<PortalIndex />} />
            <Route path="/portal/proyectos" element={<ProyectosIndex />} />
            <Route path="/portal/proyectos/:id" element={<ProyectoDetalle />} />
            <Route path="/portal/horas" element={<PortalHorasPlan />} />
            {/* Ruta legada: "Tareas" ahora es el índice de Proyectos */}
            <Route path="/portal/tareas" element={<Navigate to="/portal/proyectos" replace />} />
            <Route element={<FacturacionGuard />}>
              <Route path="/portal/facturas" element={<PortalFacturas />} />
              <Route path="/portal/cotizaciones" element={<PortalCotizaciones />} />
              <Route path="/portal/suscripcion" element={<PortalSuscripcion />} />
            </Route>
            <Route path="/portal/hosting" element={<PortalHosting />} />
            <Route path="/portal/apps" element={<PortalApps />} />
            <Route path="/portal/apps/:slug" element={<PortalAppDetalle />} />
            <Route path="/portal/nuevo-pedido" element={<PortalNuevoPedido />} />
            <Route path="/portal/nuevo-pedido/hosting" element={<PortalCheckoutHosting />} />
            <Route path="/portal/nuevo-pedido/gracias" element={<PortalCheckoutGracias />} />
            <Route path="/portal/perfil" element={<PortalPerfilLayout />}>
              <Route index element={<Navigate to="/portal/perfil/personal" replace />} />
              <Route path="personal" element={<PerfilPersonal />} />
              <Route path="empresa" element={<PerfilEmpresa />} />
              <Route path="seguridad" element={<PerfilSeguridad />} />
            </Route>
          </Route>
        </Route>
        {/* Partner — white-label pipeline + conversaciones */}
        <Route element={<PartnerGuard />}>
          <Route element={<PartnerLayout />}>
            {/* Mi cuenta (receptor): el partner ve lo que recibe de 1Conecta,
                reusando las paginas del portal. El scoping por rol ya devuelve
                su propia cuenta (su cliente_id). */}
            {/* Entra a su CRM, no a lo que le cobramos: el pipeline es lo que
                mira todos los dias. AccountSwitcher ya resolvia asi el cambio
                de cuenta; esto alinea la raiz del panel. */}
            <Route path="/partner" element={<Navigate to="/partner/pipeline" replace />} />
            <Route path="/partner/mi-cuenta" element={<PortalDashboard />} />
            <Route path="/partner/facturas" element={<PortalFacturas />} />
            <Route path="/partner/cotizaciones" element={<PortalCotizaciones />} />
            <Route path="/partner/proyectos" element={<ProyectosIndex />} />
            <Route path="/partner/proyectos/:id" element={<ProyectoDetalle />} />
            <Route path="/partner/hosting" element={<PortalHosting />} />
            <Route path="/partner/pipeline" element={<Pipeline />} />
            {/* Las cotizaciones de su CARTERA. Pagina propia y no PortalCotizaciones
                porque esa filtra por la empresa del token (la del partner), que es
                justo la que no interesa aca. */}
            <Route path="/partner/cartera/cotizaciones" element={<PartnerCotizaciones />} />
            <Route path="/partner/cartera/servicios" element={<PartnerServicios />} />
            <Route path="/partner/prospeccion" element={<Prospeccion />} />
            <Route path="/partner/tareas" element={<PartnerTareasCrm />} />
            <Route path="/partner/cotizaciones/nueva" element={<NuevaCotizacion />} />
            <Route path="/partner/cotizaciones/:id/editar" element={<NuevaCotizacion />} />
            {/* El hosting de SUS clientes, no el suyo (ese es /partner/hosting).
                Misma distincion que cartera/cotizaciones. */}
            <Route path="/partner/cartera/hosting" element={<PartnerHostingCartera />} />
            <Route path="/partner/conversaciones" element={<PartnerConversaciones />} />
            <Route path="/partner/conversaciones/bandeja" element={<Conversaciones />} />
            <Route path="/partner/conversaciones/:canal/:origen" element={<Conversaciones />} />
            <Route path="/partner/conversaciones/:canal/:origen/:convId" element={<Conversaciones />} />
            {/* Ambas paginas se comparten con el ERP. Sus endpoints ya filtran
                por partner_id; lo que sigue siendo solo-admin (tarifas, borrado)
                se oculta dentro de cada pagina segun la ruta. */}
            <Route path="/partner/empresas" element={<Clientes />} />
            <Route path="/partner/empresas/nueva" element={<NuevaEmpresa />} />
            {/* Las fichas son las mismas del ERP: se enteran del espacio por
                el pathname y recortan lo que no es del partner (ver
                TABS_PARTNER en VerEmpresa). */}
            <Route path="/partner/empresas/:id" element={<VerEmpresa />} />
            <Route path="/partner/contactos" element={<Contactos />} />
            <Route path="/partner/contactos/nuevo" element={<NuevoContacto />} />
            <Route path="/partner/contactos/:id" element={<VerContacto />} />
            <Route path="/partner/servicios" element={<Servicios />} />
            {/* Configuración: todo lo que se toca una vez y no es "yo" — la
                empresa, el equipo, la apariencia y las integraciones. Vivía
                repartido entre Ajustes y Mi perfil. */}
            <Route path="/partner/ajustes" element={<PartnerConfiguracion />}>
              <Route index element={<Navigate to="/partner/ajustes/general" replace />} />
              <Route path="general" element={<PartnerConfigGeneral />} />
              <Route path="empresa" element={<PerfilEmpresa />} />
              <Route path="pipeline" element={<PartnerConfigPipeline />} />
              <Route path="equipo" element={<PerfilEquipo />} />
            </Route>
            {/* Mi perfil: SOLO la persona — quien soy y como entro. Los datos de
                la empresa estaban aca adentro, asi que para cambiar algo que no
                es tuyo habia que entrar a "mi perfil"; se fueron a Configuracion.
                Las rutas viejas redirigen para que ningun enlace guardado muera. */}
            <Route path="/partner/perfil" element={<PartnerPerfil />} />
            <Route path="/partner/perfil/personal" element={<Navigate to="/partner/perfil" replace />} />
            <Route path="/partner/perfil/seguridad" element={<Navigate to="/partner/perfil" replace />} />
            <Route path="/partner/perfil/empresa" element={<Navigate to="/partner/ajustes/empresa" replace />} />
            <Route path="/partner/perfil/equipo" element={<Navigate to="/partner/ajustes/equipo" replace />} />
          </Route>
        </Route>

        {/* PWA Mobile: only conversaciones */}
        {isPWAMobile ? (
          <Route element={<AuthGuard />}>
            <Route element={<PWALayout />}>
              <Route path="/conversaciones" element={<Conversaciones />} />
              <Route path="/conversaciones/:canal/:origen/:convId" element={<Conversaciones />} />
              <Route path="/wiki" element={<Wiki />} />
              <Route path="/wiki/:categoriaSlug" element={<Wiki />} />
              <Route path="/wiki/:categoriaSlug/:articuloSlug" element={<Wiki />} />
              <Route index element={<Navigate to="/conversaciones" replace />} />
              <Route path="*" element={<Navigate to="/conversaciones" replace />} />
            </Route>
          </Route>
        ) : (
          /* Admin — requires authenticated admin session (desktop) */
          <Route element={<AuthGuard />}>
            <Route element={<ModuloGuard />}>
            <Route element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="prospeccion" element={<Prospeccion />} />
              <Route path="clientes" element={<Clientes />} />
              {/* Las altas van antes que las rutas :id — React Router prioriza el
                  segmento estatico, pero asi queda explicito al leerlo. */}
              <Route path="clientes/nueva" element={<NuevaEmpresa />} />
              <Route path="clientes/:id" element={<VerEmpresa />} />
              <Route path="clientes/contactos" element={<Contactos />} />
              <Route path="clientes/contactos/nuevo" element={<NuevoContacto />} />
              <Route path="clientes/contactos/:id" element={<VerContacto />} />
              <Route path="clientes/partners" element={<Partners />} />
              <Route path="clientes/partners/nuevo" element={<NuevoPartner />} />
              <Route path="clientes/accesos" element={<Accesos />} />
              <Route path="servicios" element={<Servicios />} />
              <Route path="perfiles" element={<Perfiles />} />
              <Route path="configuracion" element={<Configuracion />} />
              <Route path="marcas" element={<Marcas />} />
              <Route path="buzon" element={<Buzon />} />
              <Route path="cotizaciones" element={<Cotizaciones />} />
              <Route path="cotizaciones/nueva" element={<NuevaCotizacion />} />
              <Route path="cotizaciones/:id" element={<VerCotizacion />} />
              <Route path="cotizaciones/:id/editar" element={<NuevaCotizacion />} />
              <Route path="equipo/nueva" element={<NuevaCotEquipo />} />
              <Route path="equipo/:id" element={<VerCotizacion />} />
              <Route path="equipo/:id/editar" element={<NuevaCotEquipo />} />
              <Route path="tareas-cot/nueva" element={<NuevaCotTareas />} />
              <Route path="tareas-cot/:id" element={<DetalleCotTareas />} />
              <Route path="tareas-cot/:id/editar" element={<NuevaCotTareas />} />
              <Route path="facturas" element={<Facturas />} />
              <Route path="facturas/nueva" element={<NuevaFactura />} />
              <Route path="facturas/:id" element={<VerFactura />} />
              <Route path="facturas/:id/editar" element={<NuevaFactura />} />
              <Route path="proyectos" element={<Proyectos />} />
              <Route path="proyectos/:id" element={<VerProyecto />} />
              <Route path="proyectos/:id/:tab" element={<VerProyecto />} />
              <Route path="proyectos/:id/tareas/:tareaId" element={<VerTarea />} />
              <Route path="backlog" element={<Backlog />} />
              <Route path="hosting" element={<HostingPedidos />} />
              <Route path="redivia" element={<Redivia />} />
              <Route path="hosting/nuevo" element={<HostingNuevoServicio />} />
              <Route path="operaciones" element={<DashboardOperaciones />} />
              <Route path="crm" element={<DashboardCRM />} />
              <Route path="contabilidad" element={<DashboardContabilidad />} />
              <Route path="contabilidad/caja" element={<FlujoCaja />} />
              <Route path="contabilidad/programados" element={<PagosProgramados />} />
              <Route path="contabilidad/expedientes" element={<ExpedientesSunat />} />
              <Route path="contabilidad/comprobantes" element={<ComprobantesAgenda />} />
              <Route path="contabilidad/recurrentes" element={<PagosRecurrentes />} />
              <Route path="contabilidad/centrales" element={<CentralesCosto />} />
              <Route path="notificaciones" element={<Notificaciones />} />
              <Route path="conversaciones" element={<Conversaciones />} />
              <Route path="conversaciones/:canal/:origen/:convId" element={<Conversaciones />} />
              <Route path="agentes" element={<Agentes />} />
              {/* Apps — plataforma de micro-productos IA (Dashboard = aterrizaje) */}
              <Route path="apps" element={<Apps />} />
              <Route path="apps/:tab" element={<Apps />} />
              <Route path="activity-log" element={<ActivityLog />} />
              <Route path="marketing" element={<MarketingDashboard />} />
              <Route path="marketing/email" element={<CampanasEmail />} />
              <Route path="marketing/agenda" element={<Agendamiento />} />
              <Route path="marketing/agenda/:tab" element={<Agendamiento />} />
              <Route path="marketing/flujos" element={<FlujosMarketing />} />
              <Route path="marketing/newsletter" element={<NewsletterSuscriptores />} />
              <Route path="marketing/blog" element={<Blog />} />
              <Route path="ui-kit" element={<UiKit />} />
              <Route path="social" element={<SocialMedia />} />
              <Route path="social/:tab" element={<SocialMedia />} />
              <Route path="wiki" element={<Wiki />} />
              <Route path="wiki/:categoriaSlug" element={<Wiki />} />
              <Route path="wiki/:categoriaSlug/:articuloSlug" element={<Wiki />} />
              {/* Inmobiliaria */}
            </Route>
            </Route>
          </Route>
        )}
      </Route>
    </Routes>
  )
}

const queryClient = new QueryClient()

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
    </QueryClientProvider>
  )
}
